import EnumObject from '@/domain/enum-object.entity';
import Content from './content-interface';
import { ContractHistoryType } from './contract-history-type.enum';
import BillingDisabledHistory from './billing-disabled/billing-disabled-history.entity';
import DiscountUpdatedHistory from './discount-updated/discount-updated-history.entity';
import FeeUpdatedHistory from './fee-updated/fee-updated-history.entity';
import RulePausedHistory from './rule-paused/rule-paused-history.entity';
import FieldUpdatedHistory from './field-updated/field-updated-history.entity';
import ContractCanceledHistory from './canceled/contract-canceled-history.entity';
import ReadjustmentHistory from './readjustment/readjustment-history.entity';
import ManagementCalcHistory from './management-calculation/management-calculation-history.entity';
import ContractReOpenHistory from './reopen/contract-reopen-history';
import ContractExpirationCanceledHistory from './expiration-canceled/contract-expiration-canceled-history';
import SetPushOrderHistory from './set-push-order-history.entity';
import SetInstanceRegisteredHistory from './set-instance-registered-history.entity';
import SetInstanceDeletedHistory from './set-instance-deleted-history.entity';
import SetInstanceInactivatedHistory from './set-instance-inactivated-history.entity';
import SetInstanceActivatedHistory from './set-instance-activated-history.entity';
import SetInstanceDateChangedHistory from './set-instance-date-changed-history.entity';
import CalculationRuleApplyFailedHistory from './calculation-rule-apply-failed/calculation-rule-apply-failed-history.entity';

export default class ContractHistory {
  lastModifiedDate: string;

  lastModifiedBy: string;

  type: EnumObject;

  content: Content;

  justificationFileId: string;

  constructor(obj: any = {}) {
    this.lastModifiedBy = obj.lastModifiedBy || '';
    this.lastModifiedDate = obj.lastModifiedDate || '';
    this.type = obj.type || '';
    this.content = obj.content ? this.build(obj.type, obj.content) : '';
    this.justificationFileId = obj.justificationFileId || null;
  }

  public static of(obj: any = {}): ContractHistory {
    return new ContractHistory(obj);
  }

  public static ofList(obj: any[] = []): ContractHistory[] {
    return obj.map(this.of);
  }

  build(type: EnumObject, content: any): Content {
    switch (type.value) {
      case ContractHistoryType.BILLING_DISABLED:
        return BillingDisabledHistory.of(content);
      case ContractHistoryType.DISCOUNT_UPDATED:
      case ContractHistoryType.DISCOUNT_ASSISTED_PURCHASE:
        return DiscountUpdatedHistory.of(content);
      case ContractHistoryType.FEE_UPDATED:
        return FeeUpdatedHistory.of(content);
      case ContractHistoryType.RULE_PAUSED:
        return RulePausedHistory.of(content);
      case ContractHistoryType.FIELD_UPDATED:
        return FieldUpdatedHistory.of(content);
      case ContractHistoryType.CANCELED:
        return ContractCanceledHistory.of(content);
      case ContractHistoryType.READJUSTMENT:
        return ReadjustmentHistory.of(content);
      case ContractHistoryType.MANAGEMENT_CALCULATION:
        return ManagementCalcHistory.of(content);
      case ContractHistoryType.REOPEN:
        return ContractReOpenHistory.of(content)
      case ContractHistoryType.EXPIRATION_REQUEST_CANCELED:
        return ContractExpirationCanceledHistory.of(content)
      case ContractHistoryType.PUSH_ORDER_UPDATE:
        return SetPushOrderHistory.of(content);
      case ContractHistoryType.INSTANCE_REGISTERED:
        return SetInstanceRegisteredHistory.of(content);
      case ContractHistoryType.INSTANCE_DELETED:
        return SetInstanceDeletedHistory.of(content);
      case ContractHistoryType.INSTANCE_INACTIVATED:
        return SetInstanceInactivatedHistory.of(content);
      case ContractHistoryType.INSTANCE_ACTIVATED:
        return SetInstanceActivatedHistory.of(content);
      case ContractHistoryType.INSTANCE_DATE_CHANGED:
        return SetInstanceDateChangedHistory.of(content);
      case ContractHistoryType.CALCULATION_RULE_APPLY_FAILED:
        return CalculationRuleApplyFailedHistory.of(content);
      default:
        return content
    }
  }

  get isFieldUpdated(): boolean {
    return this.type.value === ContractHistoryType.FIELD_UPDATED
  }

  get isDiscountUpdated(): boolean {
    return this.type.value === ContractHistoryType.DISCOUNT_UPDATED
      || this.type.value === ContractHistoryType.DISCOUNT_ASSISTED_PURCHASE
  }

  get isFeeUpdated(): boolean {
    return this.type.value === ContractHistoryType.FEE_UPDATED
  }

  get isDiscountOrFeeUpdated(): boolean {
    return this.isDiscountUpdated || this.isFeeUpdated;
  }

  get isReadjustment(): boolean {
    return this.type.value === ContractHistoryType.READJUSTMENT
  }

  get isManagementCalculation(): boolean {
    return this.type.value === ContractHistoryType.MANAGEMENT_CALCULATION
  }

  get isReOpenContract(): boolean {
    return this.type.value === ContractHistoryType.REOPEN
  }

  get isExpirationRequestCanceled(): boolean {
    return this.type.value === ContractHistoryType.EXPIRATION_REQUEST_CANCELED
  }

  get isSetPushOrder(): boolean {
    return this.type.value === ContractHistoryType.PUSH_ORDER_UPDATE
  }

  get isSetInstance(): boolean {
    return this.type.value === ContractHistoryType.INSTANCE_REGISTERED
      || this.type.value === ContractHistoryType.INSTANCE_DELETED
      || this.type.value === ContractHistoryType.INSTANCE_INACTIVATED
      || this.type.value === ContractHistoryType.INSTANCE_ACTIVATED
  }

  get isInstanceDateChanged(): boolean {
    return this.type.value === ContractHistoryType.INSTANCE_DATE_CHANGED
  }

  get isPreviewSwitched(): boolean {
    return this.type.value === ContractHistoryType.PREVIEW_SWITCHED
  }

  get isCalculationRuleApplyFailed(): boolean {
    return this.type.value === ContractHistoryType.CALCULATION_RULE_APPLY_FAILED
  }
}
